import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, SearchClear } from '../../_Components';
import { GET_LIST, provider } from '../../_utils';
import {
    fullName,
    operatorId,
} from '../../operators/format';

const asId = (value) => {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    return String(value.QueueId || value.id || value._id || value.queueId || '');
};

const normQueueId = (value) => String(value || '').replace(/[{}]/g, '').toLowerCase();

const queueIdsOf = (row) => {
    if (row === undefined || row === null || row === '') {
        return [];
    }
    if (typeof row === 'string' || typeof row === 'number') {
        const id = normQueueId(row);
        return id ? [id] : [];
    }
    return [row.QueueId, row.id, row._id, row.queueId]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map((value) => normQueueId(value))
        .filter(Boolean);
};

const sameQueue = (a, b) => {
    const right = queueIdsOf(b);
    if (!right.length) {
        return false;
    }
    return queueIdsOf(a).some((id) => right.indexOf(id) >= 0);
};

const idsOf = (value) => (Array.isArray(value) ? value.map(asId).filter(Boolean) : []);

const hasId = (list, id, queues) => (Array.isArray(list) ? list : []).some((item) => (
    queues ? sameQueue(item, id) : String(asId(item)) === String(asId(id))
));

const queueName = (row) => {
    if (!row) {
        return '';
    }
    return row.name || row.DisplayName || row.Name || row.QueueName || row.queueName || row.title || '';
};

const queueRecordId = (row) => row && (row.QueueId || row.id || row._id || row.queueId);

const queueTypeOf = (row) => {
    if (!row) {
        return '';
    }
    return row.type || row.QueueType || row.queueType || '';
};

const mapInfraQueue = (row) => {
    if (!row) {
        return null;
    }
    return {
        ...row,
        id: queueRecordId(row),
        name: queueName(row),
        type: queueTypeOf(row)
    };
};

const findQueue = (catalog, id) => catalog.find((item) => sameQueue(item, id)) || null;

const isOutboundQueue = (row) => {
    const type = String(queueTypeOf(row)).toLowerCase();
    return type === 'outbound' || type === 'out' || type === '1';
};

const queueMatches = (row, query) => {
    if (!query) {
        return true;
    }
    const name = queueName(row).toLowerCase();
    const kind = isOutboundQueue(row) ? 'исходящ' : 'входящ';
    return name.indexOf(query) >= 0 || kind.indexOf(query) >= 0;
};

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const normalizeQuery = (value) => String(value || '').trim().toLowerCase();

const operatorMatches = (row, query) => {
    if (!query) {
        return true;
    }
    const name = fullName(row).toLowerCase();
    const login = String(row.login || '').toLowerCase();
    return name.indexOf(query) >= 0 || login.indexOf(query) >= 0;
};

const isAdminDept = (department) => {
    const value = String(department || '').toLowerCase();
    return value === 'administration' || value === 'administaration';
};

const queueTypeMeta = (type) => (
    type === 'Outbound'
        ? { label: 'Исходящая', chip: 'pc-emp-chip--warn' }
        : { label: 'Входящая', chip: 'pc-emp-chip--acc' }
);

const AssignField = ({ input, kind, items, options = [] }) => {
    const selected = idsOf(input && input.value);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(0);
    const [fetched, setFetched] = useState([]);
    const wrapRef = useRef(null);
    const isQueues = kind === 'queues';
    const fromProps = Array.isArray(items) ? items : options;

    useEffect(() => {
        if (!isQueues) {
            return undefined;
        }
        let alive = true;
        const load = (path) => provider(
            GET_LIST,
            path,
            {
                filter: {},
                sort: { field: 'name', order: 'ASC' },
                pagination: { page: 1, perPage: 10000 },
            },
            { persist: true }
        ).then((result) => (result && result.data ? result.data : []).map(mapInfraQueue).filter(Boolean))
            .catch(() => []);

        Promise.all([load('/infra/queues'), load('/infra/queues/list')]).then(([raw, listed]) => {
            if (!alive) {
                return;
            }
            setFetched(raw.concat(listed));
        });
        return () => { alive = false; };
    }, [isQueues]);

    const catalog = useMemo(() => {
        const propsList = (Array.isArray(fromProps) ? fromProps : []).filter(Boolean);
        if (!isQueues) {
            return propsList;
        }
        const byId = {};
        const remember = (item) => {
            if (!item) {
                return;
            }
            const mapped = mapInfraQueue(item);
            const keys = queueIdsOf(mapped);
            if (!keys.length) {
                return;
            }
            let prev = null;
            keys.forEach((key) => {
                if (byId[key]) {
                    prev = byId[key];
                }
            });
            const next = {
                ...(prev || {}),
                ...mapped,
                name: queueName(mapped) || queueName(prev) || '',
                type: queueTypeOf(mapped) || queueTypeOf(prev) || '',
            };
            queueIdsOf(next).forEach((key) => {
                byId[key] = next;
            });
        };
        propsList.map(mapInfraQueue).concat(fetched).forEach(remember);
        const seen = [];
        const list = [];
        Object.keys(byId).forEach((key) => {
            const item = byId[key];
            if (seen.indexOf(item) >= 0) {
                return;
            }
            seen.push(item);
            list.push(item);
        });
        return list;
    }, [fromProps, fetched, isQueues]);

    const assignedRows = useMemo(() => {
        const rawList = Array.isArray(input && input.value) ? input.value : selected;
        return rawList.map((item) => {
            const id = asId(item);
            if (!id) {
                return null;
            }
            const fromValue = item && typeof item === 'object' ? mapInfraQueue(item) : null;
            const row = isQueues
                ? (findQueue(catalog, item) || fromValue)
                : (catalog.find((entry) => asId(operatorId(entry)) === id) || fromValue);
            return { id, row };
        }).filter(Boolean);
    }, [selected, catalog, isQueues, input]);

    const suggestions = useMemo(() => {
        const q = normalizeQuery(query);
        if (isQueues) {
            const matched = catalog.filter((item) => {
                const id = asId(item);
                if (!id || hasId(input && input.value, item, true) || hasId(selected, item, true)) {
                    return false;
                }
                return queueMatches(item, q);
            });
            const inbound = matched.filter((item) => !isOutboundQueue(item));
            const outbound = matched.filter((item) => isOutboundQueue(item));
            const cap = q ? 30 : 15;
            return inbound.slice(0, cap).concat(outbound.slice(0, cap));
        }
        return catalog.filter((item) => {
            const id = asId(operatorId(item));
            if (!id || hasId(selected, id, false)) {
                return false;
            }
            if (item.status && item.status !== 'Active') {
                return false;
            }
            if (isAdminDept(item.department)) {
                return false;
            }
            return operatorMatches(item, q);
        }).slice(0, 20);
    }, [catalog, isQueues, query, selected, input]);

    useEffect(() => {
        setHi(0);
    }, [query, open]);

    useEffect(() => {
        const onDoc = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const add = (item) => {
        const id = asId(isQueues ? item : operatorId(item));
        if (!id || hasId(selected, id, isQueues)) {
            return;
        }
        const next = [id].concat(idsOf(input && input.value));
        if (input && input.onChange) {
            input.onChange(next);
        }
        setQuery('');
        setOpen(false);
    };

    const remove = (id) => {
        const rawList = Array.isArray(input && input.value) ? input.value : selected;
        const next = rawList.filter((item) => (
            isQueues
                ? !sameQueue(item, id)
                : String(asId(item)) !== String(id)
        ));
        if (input && input.onChange) {
            input.onChange(next);
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setHi((n) => Math.min(n + 1, Math.max(0, suggestions.length - 1)));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHi((n) => Math.max(n - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            if (suggestions[hi]) {
                add(suggestions[hi]);
            }
            return;
        }
        if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    const emptyTitle = isQueues ? 'Очереди не назначены' : 'Операторы не назначены';
    const emptyText = isQueues
        ? 'Добавьте очереди контакт-центра, которые обслуживает эта компания.'
        : 'Добавьте сотрудников, которые могут принимать звонки этой компании.';
    const placeholder = isQueues ? 'Найти очередь КЦ' : 'Найти оператора';

    return (
        <div className="pc-assign">
            <div className="pc-assign-add" ref={wrapRef}>
                <div className="pc-emp-srch">
                    <Icon d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM10.5 10.5L14 14" />
                    <input
                        className="pc-emp-inp"
                        value={query}
                        placeholder={placeholder}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={onKeyDown}
                    />
                    <SearchClear
                        visible={Boolean(query)}
                        onClear={() => {
                            setQuery('');
                            setOpen(true);
                        }}
                    />
                </div>
                {open && (
                    <div className="pc-assign-drop" role="listbox">
                        {suggestions.length === 0 ? (
                            <div className="pc-assign-drop-empty">
                                {query ? 'Ничего не найдено' : (isQueues ? 'Все очереди уже добавлены' : 'Начните вводить имя или логин')}
                            </div>
                        ) : suggestions.map((item, index) => {
                            if (isQueues) {
                                const type = queueTypeMeta(isOutboundQueue(item) ? 'Outbound' : item.type);
                                const prev = index > 0 ? suggestions[index - 1] : null;
                                const showHead = !prev || isOutboundQueue(prev) !== isOutboundQueue(item);
                                return (
                                    <Fragment key={asId(item) || index}>
                                        {showHead && (
                                            <div className="pc-assign-drop-h">
                                                {isOutboundQueue(item) ? 'Исходящие' : 'Входящие'}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className={index === hi ? 'is-on' : ''}
                                            onMouseEnter={() => setHi(index)}
                                            onClick={() => add(item)}
                                        >
                                            <span>{queueName(item) || asId(item) || '—'}</span>
                                            <i>{type.label}</i>
                                        </button>
                                    </Fragment>
                                );
                            }
                            const name = fullName(item);
                            return (
                                <button
                                    type="button"
                                    key={operatorId(item) || index}
                                    className={index === hi ? 'is-on' : ''}
                                    onMouseEnter={() => setHi(index)}
                                    onClick={() => add(item)}
                                >
                                    <span>{name || '—'}</span>
                                    <i>{item.login || '—'}</i>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {assignedRows.length === 0 ? (
                <EmptyState title={emptyTitle} description={emptyText} />
            ) : (
                <div className="pc-assign-list">
                    {assignedRows.map(({ id, row }) => {
                        if (isQueues) {
                            const type = queueTypeMeta(isOutboundQueue(row) ? 'Outbound' : (row && row.type));
                            const name = queueName(row) || id || '—';
                            return (
                                <div className="pc-assign-row" key={id}>
                                    <span>{name}</span>
                                    <i>{type.label}</i>
                                    <button
                                        type="button"
                                        className="pc-assign-x"
                                        onClick={() => remove(id)}
                                        aria-label="Удалить очередь"
                                    >
                                        <Icon d="M4 4l8 8M12 4l-8 8" />
                                    </button>
                                </div>
                            );
                        }
                        const name = row ? fullName(row) : id;
                        return (
                            <div className={`pc-assign-row${row && row.status === 'Terminated' ? ' off' : ''}`} key={id}>
                                <span>{name || '—'}</span>
                                <i>{(row && row.login) || id}</i>
                                <button
                                    type="button"
                                    className="pc-assign-x"
                                    onClick={() => remove(id)}
                                    aria-label="Удалить оператора"
                                >
                                    <Icon d="M4 4l8 8M12 4l-8 8" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AssignField;
